import { useState, useEffect } from "react";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Modal,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../Common/styles/table";
import {
  ModalDrawerStyles,
  ModalDrawerButtonStyles,
  ModalHeaderTitleStyles,
} from "../../Common/styles/modal";
import {
  InfoOutlined,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  CloseRounded,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";

import { formatRolesAndPermissions } from "../../../utils/roles";
// import AddRoleModal from "./Modals/AddRoleModal";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../Redux/hooks";
import { setStateToggle } from "../../../Redux/reducers/userSlice";

interface PermissionMatrixProps {
  open: boolean;
  onClose: () => void;
}

const PermissionMatrix = ({ open, onClose }: PermissionMatrixProps) => {
  const { id } = useParams();

  const [toggleLoader, setToggleLoader] = useState(false);
  const [editPermissions, setEditPermissions] = useState(false);
  const [data, setData] = useState<any>({ headers: [], rows: [] });
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [submitLoader, setSubmitLoader] = useState(false);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleEditPermissions = () => {
    refreshPage();
    setEditPermissions(true);
  };

  const handleCloseEditPermissions = () => {
    setEditPermissions(false);
    refreshPage();
  };

  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        setLoading(true);
        const res1: AxiosResponse = await http.get(`/platform/roles/matrix`);
        const res2: AxiosResponse = await http.get(
          `/admin/permission?type=platform`
        );
        const rolesData = res1?.data?.data;
        const permissionsData = res2?.data?.data;

        const formattedData = formatRolesAndPermissions(
          rolesData,
          permissionsData
        );
        setData(formattedData || { headers: [], rows: [] });
        setLoading(false);
      } catch (err) {
        setData({ headers: [], rows: [] });
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchUsersData();
  }, [toggleLoader, id, setLoading]);

  const handleCheckboxToggle = (permissionId: string, roleId: string) => {
    setData((prevData: any) => {
      const updatedRows = prevData.rows.map((row: any) => {
        if (row.id === permissionId) {
          return {
            ...row,
            [roleId]: !row[roleId],
          };
        }
        return row;
      });
      return { ...prevData, rows: updatedRows };
    });
  };

  const saveData = async () => {
    try {
      setSubmitLoader(true);

      const roles = data?.headers?.map((role: any) => {
        const permissionsObj: any = {};
        data?.rows?.forEach((row: any) => {
          permissionsObj[row.id] = row[role.id] || false;
        });
        return {
          roleId: role.id,
          permissions: permissionsObj,
        };
      });

      const body = {
        roles,
      };

      let res: AxiosResponse;
      res = await http.put(`/platform/roles/matrix`, body);
      toastMessage("success", res?.data?.messages);
      setSubmitLoader(false);
      handleCloseEditPermissions();
      dispatch(setStateToggle());
    } catch (err) {
      setSubmitLoader(false);
      errorToastMessage(err as Error);
    }
  };
  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={ModalDrawerStyles}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={ModalHeaderTitleStyles}>
            {editPermissions ? "Edit Platform Rights" : "Platform Rights"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!editPermissions && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleEditPermissions}
                startIcon={<EditIcon />}
              >
                Edit Platform Rights
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseRounded sx={{ color: "text.secondary" }} />
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
          <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={TableBorderRadiusTopLeftRight}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell
                      sx={{
                        minWidth: 230,
                        maxWidth: 250,
                        position: "sticky",
                        zIndex: 10,
                        left: 0,
                      }}
                    ></StyledTableCell>
                    {!loading &&
                      data?.headers?.length > 0 &&
                      data?.headers?.map((role: any) => (
                        <StyledTableCell key={role.id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              maxWidth: "120px",
                            }}
                          >
                            <Typography
                              overflow={"hidden"}
                              textOverflow={"ellipsis"}
                              whiteSpace={"nowrap"}
                              title={role?.label}
                              fontWeight={"bold"}
                              variant="body1"
                            >
                              {role?.label}{" "}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                      ))}
                  </TableRow>
                </TableHead>
                {!loading && data?.rows?.length > 0 && (
                  <TableBody>
                    {data?.rows?.map((permission: any) => (
                      <TableRow key={permission.id}>
                        <StyledTableCell
                          sx={{ position: "sticky", zIndex: 10, left: 0 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              maxWidth: "280px",
                            }}
                          >
                            <Typography
                              overflow={"hidden"}
                              textOverflow={"ellipsis"}
                              whiteSpace={"nowrap"}
                              fontWeight={"medium"}
                              title={permission?.label}
                            >
                              {permission.label}
                            </Typography>
                            <Tooltip title={permission?.description} arrow>
                              <IconButton
                                size="small"
                                sx={{ cursor: "default" }}
                              >
                                <InfoOutlined />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </StyledTableCell>
                        {data?.headers?.map((role: any) => (
                          <StyledTableCell
                            key={role.id}
                            sx={{ minWidth: "170px", maxWidth: "190px" }}
                          >
                            {!editPermissions ? (
                              permission?.[role?.id] ? (
                                <Box sx={{ display: "flex", ml: 0.5 }}>
                                  <Check
                                    sx={{ color: "rgba(0, 0, 0, 0.54)" }}
                                  />
                                </Box>
                              ) : (
                                <Typography>—</Typography>
                              )
                            ) : (
                              <Checkbox
                                checked={permission?.[role?.id]}
                                icon={<CheckBoxOutlineBlank />}
                                onChange={() =>
                                  handleCheckboxToggle(permission?.id, role?.id)
                                }
                                checkedIcon={<CheckBox />}
                              />
                            )}
                          </StyledTableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
            </Box>

            {!loading && data?.row?.length === 0 && (
              <NoDataContainer>
                <Typography variant="body1" color="gray">
                  No Data
                </Typography>
              </NoDataContainer>
            )}
            {loading && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
          </Box>
        </Box>

        {editPermissions && (
          <Box sx={ModalDrawerButtonStyles}>
            {!submitLoader ? (
              <>
                <Button variant="outlined" onClick={handleCloseEditPermissions}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={saveData}>
                  Save
                </Button>
              </>
            ) : (
              <CircularProgress size={25} />
            )}
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default PermissionMatrix;
